// utils/fetchData.js

const API_URL = "https://json-backend.vercel.app/json/6891f64b729c0018e3febefd";

/*
 * Put the fallback file here:
 *
 * public/api.json
 *
 * Using "/api.json" ensures it works from nested routes such as:
 * /table/tbtmeeting
 */
const FALLBACK_API_URL = "/api.json";

const STORAGE_KEY = "apiData";
const STORAGE_META_KEY = "apiDataMeta";

const REMOTE_TIMEOUT_MS = 5_000;
const FALLBACK_TIMEOUT_MS = 5_000;

let activeRequest = null;

/**
 * Check whether browser APIs are available.
 */
const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

/**
 * Returns true when an error was caused by an aborted request.
 */
const isAbortError = (error) =>
  error?.name === "AbortError" ||
  error?.name === "TimeoutError" ||
  error?.code === "ABORT_ERR";

/**
 * Extract data from either:
 * { data: ... }
 *
 * or:
 * direct JSON content
 */
const extractData = (result) => {
  if (result === null || result === undefined) {
    throw new Error("The response does not contain usable data.");
  }

  if (typeof result !== "object") {
    throw new Error("The response is not valid JSON data.");
  }

  const data = Object.prototype.hasOwnProperty.call(result, "data")
    ? result.data
    : result;

  if (data === null || data === undefined) {
    throw new Error("The response does not contain usable data.");
  }

  return data;
};

/**
 * Safely store data and cache information.
 */
const storeData = (data, { source, isFallback = false } = {}) => {
  if (!isBrowser()) return;

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

    localStorage.setItem(
      STORAGE_META_KEY,
      JSON.stringify({
        fetchedAt: new Date().toISOString(),
        source: source || API_URL,
        isFallback,
      }),
    );
  } catch (error) {
    console.error("Unable to save API data to localStorage:", error);
  }
};

/**
 * Fetch JSON with timeout and optional external cancellation.
 */
const fetchJsonWithTimeout = async (url, { timeoutMs, signal }) => {
  const controller = new AbortController();
  let didTimeout = false;

  const handleExternalAbort = () => {
    controller.abort(signal?.reason);
  };

  if (signal) {
    if (signal.aborted) {
      controller.abort(signal.reason);
    } else {
      signal.addEventListener("abort", handleExternalAbort, { once: true });
    }
  }

  const timeoutId = window.setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(
        `Request failed: ${response.status} ${response.statusText}`,
      );
    }

    const contentType = response.headers.get("content-type") || "";

    if (
      contentType &&
      !contentType.toLowerCase().includes("application/json")
    ) {
      throw new Error(`Expected JSON but received "${contentType}".`);
    }

    try {
      return await response.json();
    } catch {
      throw new Error(`The response from "${url}" is not valid JSON.`);
    }
  } catch (error) {
    if (didTimeout) {
      const timeoutError = new Error(`Request timed out after ${timeoutMs}ms.`);

      timeoutError.name = "TimeoutError";
      throw timeoutError;
    }

    throw error;
  } finally {
    window.clearTimeout(timeoutId);

    signal?.removeEventListener?.("abort", handleExternalAbort);
  }
};

/**
 * Fetch the remote data.
 * If the remote server is slow or unavailable,
 * automatically use public/api.json.
 *
 * @param {Object} options
 * @param {AbortSignal} options.signal
 * @returns {Promise<unknown>}
 */
export const fetchAndStoreData = async ({ signal } = {}) => {
  if (!isBrowser()) return null;

  // Prevent duplicate requests from React StrictMode or
  // multiple components requesting the same resource.
  if (activeRequest) {
    return activeRequest;
  }

  activeRequest = (async () => {
    try {
      const remoteResult = await fetchJsonWithTimeout(API_URL, {
        timeoutMs: REMOTE_TIMEOUT_MS,
        signal,
      });

      const remoteData = extractData(remoteResult);

      storeData(remoteData, {
        source: API_URL,
        isFallback: false,
      });

      return remoteData;
    } catch (remoteError) {
      /*
       * Do not start the fallback request when the caller
       * intentionally cancelled the operation.
       */
      if (signal?.aborted) {
        throw remoteError;
      }

      console.warn(
        "Remote API was slow or unavailable. Loading local api.json instead.",
        remoteError,
      );

      try {
        const fallbackResult = await fetchJsonWithTimeout(FALLBACK_API_URL, {
          timeoutMs: FALLBACK_TIMEOUT_MS,
          signal,
        });

        const fallbackData = extractData(fallbackResult);

        storeData(fallbackData, {
          source: FALLBACK_API_URL,
          isFallback: true,
        });

        return fallbackData;
      } catch (fallbackError) {
        if (signal?.aborted || isAbortError(fallbackError)) {
          throw fallbackError;
        }

        const cachedData = getStoredData();

        /*
         * Keep the application usable when both sources fail,
         * provided cached data already exists.
         */
        if (cachedData !== null) {
          console.warn("Remote and local sources failed. Using cached data.");

          return cachedData;
        }

        throw new Error(
          "Unable to load data from the remote API or local api.json.",
          {
            cause: fallbackError,
          },
        );
      }
    }
  })();

  try {
    return await activeRequest;
  } finally {
    activeRequest = null;
  }
};

/**
 * Read cached API data.
 *
 * @returns {unknown | null}
 */
export const getStoredData = () => {
  if (!isBrowser()) return null;

  try {
    const storedData = localStorage.getItem(STORAGE_KEY);

    if (!storedData) return null;

    return JSON.parse(storedData);
  } catch (error) {
    console.error("Unable to read stored API data:", error);

    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_META_KEY);

    return null;
  }
};

/**
 * Read cache metadata.
 *
 * @returns {{
 *   fetchedAt: string,
 *   source: string,
 *   isFallback: boolean
 * } | null}
 */
export const getStoredDataMeta = () => {
  if (!isBrowser()) return null;

  try {
    const storedMeta = localStorage.getItem(STORAGE_META_KEY);

    if (!storedMeta) return null;

    return JSON.parse(storedMeta);
  } catch (error) {
    console.error("Unable to read stored API metadata:", error);

    localStorage.removeItem(STORAGE_META_KEY);

    return null;
  }
};

/**
 * Remove cached API data.
 */
export const clearStoredData = () => {
  if (!isBrowser()) return;

  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_META_KEY);
};

/**
 * Load fresh data when the application starts.
 */
const initializeApiData = () => {
  fetchAndStoreData().catch((error) => {
    if (isAbortError(error)) {
      console.warn("API data request was cancelled.");
      return;
    }

    console.error("Unable to initialize API data:", error);
  });
};

if (isBrowser()) {
  if (document.readyState === "loading") {
    window.addEventListener("DOMContentLoaded", initializeApiData, {
      once: true,
    });
  } else {
    initializeApiData();
  }
}
